import {
  useToast,
  Text,
  Box,
  Heading,
  Accordion,
  Checkbox,
  Flex,
  AccordionItem,
  AccordionButton,
  AccordionIcon,
  AccordionPanel,
  useDisclosure,
  InputGroup,
  Input,
  InputLeftAddon,
} from '@chakra-ui/react';
import Editor from '@monaco-editor/react';
import React, { useCallback, useState } from 'react';

import {
  finleyAssert,
  utils as commonUtils,
  path as commonPath,
} from '@finley-technologies/common';
import { FinleyAi, typeguards } from '@finley-technologies/finley-types';

import { FinleyAiViewContext } from './types';
import {
  AddIconButton,
  DeleteIconButton,
  DownloadIconButton,
  UploadIconButton,
} from 'packages/common-frontend/components/IconButton';
import { UploadModal } from 'packages/common-frontend/components/ReactFlow/UploadModal';
import { TextWithCopy } from 'packages/common-frontend/components/TextWithCopy';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { useFinleyAiDocumentAmaDirectUpload } from 'packages/finleyAi/hooks/documentAmaDirectUpload';
import {
  useGetKnowledgeBaseDocumentsByOrganizationIdQuery,
  useGetKnowledgeBaseDocumentSignedUrlLazyQueryGQLType,
} from 'packages/finleyAi/requests/ai.gql.generated';

const DocumentTable = (params: {
  knowledgeBaseDocument: FinleyAi.FinleyAiKnowledgeBaseDocument;
  isSelected: boolean;
  onToggleDocumentSelect: (
    document: FinleyAi.FinleyAiKnowledgeBaseDocument,
  ) => void;
}) => {
  const toast = useToast();
  const { knowledgeBaseDocument, isSelected, onToggleDocumentSelect } = params;

  const [
    getKnowledgeBaseDocumentSignedUrl,
  ] = useGetKnowledgeBaseDocumentSignedUrlLazyQueryGQLType({
    variables: {
      serializedParams: JSON.stringify({
        knowledgeBaseDocumentId: knowledgeBaseDocument.id,
      }),
    },
    onCompleted: ({ getSignedDownloadUrls }) => {
      if (getSignedDownloadUrls.length === 1) {
        const { url, filename } = getSignedDownloadUrls[0];

        // download
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = filename;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
      } else {
        toast({
          ...errorToastConfig,
          title: `An error occurred while attempting to download document.`,
        });
      }
    },
    onError: () => {
      toast({
        ...errorToastConfig,
        title: `An error occurred while attempting to download document.`,
      });
    },
  });

  return (
    <AccordionItem>
      <Flex justifyContent="space-between" alignItems="center">
        <Flex>
          <Checkbox
            type="checkbox"
            isChecked={isSelected}
            onChange={() => onToggleDocumentSelect(knowledgeBaseDocument)}
            mr="2"
          />
          <AccordionButton p="2">
            <AccordionIcon />
          </AccordionButton>
        </Flex>

        <Flex w="500px" alignItems="center">
          <TextWithCopy
            label={knowledgeBaseDocument.documentName}
            styleProps={{ noOfLines: 1 }}
          >
            {knowledgeBaseDocument.documentName}
          </TextWithCopy>
          <DownloadIconButton
            label="download"
            ariaLabel="download"
            onClick={() => getKnowledgeBaseDocumentSignedUrl()}
          />
        </Flex>
      </Flex>

      <AccordionPanel pb={4}>
        <Text key={`${knowledgeBaseDocument.id}-documentType`} fontSize="xs">
          Document type: {knowledgeBaseDocument.documentType}
        </Text>
        <Text key={`${knowledgeBaseDocument.id}-documentTags`} fontSize="xs">
          Document tags: {JSON.stringify(knowledgeBaseDocument.tags)}
        </Text>
      </AccordionPanel>
    </AccordionItem>
  );
};

type FinleyAiDocumentAmaProps = {
  targetOrganizationId: string;
  selectedKnowledgeBaseDocumentIds: string[];
  isLocked: boolean;
  viewContext: FinleyAiViewContext;
  setViewContext: (value: React.SetStateAction<FinleyAiViewContext>) => void;
  handleDocumentAmaUpload: ({
    documentName,
    s3Bucket,
    s3Path,
  }: {
    documentName: string;
    s3Bucket: string;
    s3Path: string;
  }) => void;
};

export const FinleyAiDocumentAma = ({
  targetOrganizationId,
  selectedKnowledgeBaseDocumentIds,
  isLocked,
  viewContext,
  setViewContext,
  handleDocumentAmaUpload,
}: FinleyAiDocumentAmaProps) => {
  const toast = useToast();

  const [documentName, setDocumentName] = useState<string>('');
  const { isOpen, onClose, onOpen } = useDisclosure();

  const { directUpload } = useFinleyAiDocumentAmaDirectUpload({
    targetOrganizationId,
  });

  finleyAssert(
    viewContext.type === 'documentAma',
    'View context must be document AMA.',
  );

  const {
    data: knowledgeBaseDocumentsData,
  } = useGetKnowledgeBaseDocumentsByOrganizationIdQuery({
    onError: (err) => {
      const toastConfig = { ...errorToastConfig };
      toastConfig.title = commonUtils.EnvUtils.isCustomerFacing()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(toastConfig);
    },
    variables: { organizationId: targetOrganizationId },
    fetchPolicy: 'network-only',
  });

  const onToggleDocumentSelect = useCallback(
    (document: FinleyAi.FinleyAiKnowledgeBaseDocument) => {
      finleyAssert(
        viewContext.type === 'documentAma',
        'Unable to set table selection.',
      );

      if (isLocked) {
        toast({
          title: 'Start a new thread to change documents.',
          position: 'top',
          duration: 6000,
          isClosable: true,
          status: 'info',
        });
        return;
      }
      const isDocumentSelected = selectedKnowledgeBaseDocumentIds.some(
        (knowledgeBaseDocumentId) => knowledgeBaseDocumentId === document.id,
      );

      const newDocumentSelections = isDocumentSelected
        ? selectedKnowledgeBaseDocumentIds.filter(
            (knowledgeBaseDocumentId) =>
              knowledgeBaseDocumentId !== document.id,
          )
        : selectedKnowledgeBaseDocumentIds.concat([document.id]);

      setViewContext((prevState) => ({
        ...prevState,
        knowledgeBaseDocumentIds: newDocumentSelections,
      }));
    },
    [
      isLocked,
      selectedKnowledgeBaseDocumentIds,
      toast,
      viewContext,
      setViewContext,
    ],
  );

  const handleToolChange = useCallback(
    (changedTool: string) => {
      setViewContext((prevState) => ({
        ...prevState,
        tool: changedTool,
      }));
    },
    [setViewContext],
  );

  const upload = useCallback(
    async (file: File) => {
      if (documentName !== '') {
        try {
          const fileExtension = commonPath.getFileExtension(file.name);

          finleyAssert(
            typeguards.finleyAi.isFinleyAiTextractSupportedFileExtension(
              fileExtension,
            ),
            `[fileExtension:${fileExtension}] is not a supported file extension.`,
          );
          const { s3Bucket, s3Path } = await directUpload({
            file,
            documentName,
            fileExtension,
          });

          await handleDocumentAmaUpload({ documentName, s3Bucket, s3Path });

          setDocumentName('');
        } catch (e) {
          toast({
            title: 'Document upload failed. Please see technical assistance.',
            status: 'error',
            position: 'top',
            isClosable: true,
          });
        }
      } else {
        toast({
          title: 'Document Name must be specified.',
          status: 'error',
          position: 'top',
          isClosable: true,
        });
      }
    },
    [directUpload, toast, documentName, handleDocumentAmaUpload],
  );

  const addKeyword = () => {
    setViewContext((prev) => {
      finleyAssert(
        prev.type === 'documentAma',
        'Unable to set table selection.',
      );

      const keywords = [...prev.keywords];
      keywords.push('');
      return { ...prev, keywords };
    });
  };

  const handleKeywordChange = ({ val, idx }: { val: string; idx: number }) => {
    setViewContext((prev) => {
      finleyAssert(
        prev.type === 'documentAma',
        'Unable to set table selection.',
      );

      const keywords = [...prev.keywords];
      keywords[idx] = val;
      return { ...prev, keywords };
    });
  };

  const deleteKeyword = (idx: number) => {
    setViewContext((prev) => {
      finleyAssert(
        prev.type === 'documentAma',
        'Unable to set table selection.',
      );

      const keywords = [...prev.keywords];
      keywords[idx] = keywords[keywords.length - 1];
      keywords.pop();
      return { ...prev, keywords };
    });
  };

  if (!knowledgeBaseDocumentsData) {
    return <Text>Loading documents...</Text>;
  }

  const knowledgeBaseDocuments =
    knowledgeBaseDocumentsData.getKnowledgeBaseDocumentsByOrganizationId
      .knowledgeBaseDocuments;

  return (
    <Box mr="20px">
      <Flex alignItems="center" mb="3">
        <Heading as="h4" size="md" mr="3">
          Documents
        </Heading>
        <UploadIconButton
          ariaLabel="upload"
          label="upload"
          onClick={onOpen}
          isDisabled={isLocked}
        />
        <UploadModal
          modalHeaderText="Upload Document"
          onClose={onClose}
          isOpen={isOpen}
          handleFile={upload}
          allowedExtensions={
            FinleyAi.FinleyAiTextractSupportedFileExtensionIterable
          }
        >
          <InputGroup mb="3">
            <InputLeftAddon>Document Name</InputLeftAddon>
            <Input
              type="text"
              placeholder="documentName"
              value={documentName}
              onChange={(e) => {
                setDocumentName(e.target.value);
              }}
            />
          </InputGroup>
        </UploadModal>
      </Flex>
      <Accordion allowToggle w="xl" mb="10">
        {knowledgeBaseDocuments.map((knowledgeBaseDocument) => {
          const isSelected = selectedKnowledgeBaseDocumentIds.some(
            (knowledgeBaseDocumentId) =>
              knowledgeBaseDocumentId === knowledgeBaseDocument.id,
          );
          return (
            <DocumentTable
              key={knowledgeBaseDocument.id}
              knowledgeBaseDocument={knowledgeBaseDocument}
              isSelected={isSelected}
              onToggleDocumentSelect={onToggleDocumentSelect}
            />
          );
        })}
      </Accordion>
      <Heading as="h4" size="md" mr="3">
        Tool (jsonschema)
      </Heading>
      <Editor
        value={viewContext.tool}
        onChange={(val) => handleToolChange(val ?? '')}
        theme="vs-dark"
        width="100%"
        height="400px"
      />
      <Flex alignItems="center">
        <Heading as="h4" size="md">
          Keywords
        </Heading>
        <AddIconButton onClick={addKeyword} />
      </Flex>
      {viewContext.keywords.map((keyword, idx) => {
        return (
          <Flex
            // eslint-disable-next-line react/no-array-index-key
            key={`keyword-input-${idx}`}
          >
            <DeleteIconButton onClick={() => deleteKeyword(idx)} />
            <Input
              value={keyword}
              onChange={(e) =>
                handleKeywordChange({ val: e.target.value, idx })
              }
            />
          </Flex>
        );
      })}
    </Box>
  );
};
